import * as R from 'rambdax'
import {useMemo} from 'react'
import {type PropDef, type SearchTypes, type TypeInfos, logger} from 'tizra'
import {useApi} from './useApi'

const log = logger('useTypeInfo')

export const upgradeTypeInfoToSearchTypes = (
  typeInfo: TypeInfos,
): SearchTypes | null => {
  let missingSuperTypes = false
  const allSuperTypes = new Set(
    Object.values(typeInfo).flatMap(t => t.superTypeAncestry || []),
  )
  for (const m of allSuperTypes) {
    if (!typeInfo[m]) {
      log.once(m)?.error(`type-info missing super-type: ${m}`)
      missingSuperTypes = true
    }
  }
  if (missingSuperTypes) return null

  // Clone so we can mutate.
  const searchTypes: SearchTypes = R.piped(
    R.clone(typeInfo),
    R.values,
    R.map(({propDefs, ...typeDef}) => ({
      ...typeDef,
      propDefsIncludingSubtypes: {},
    })),
    R.sortBy(searchType => searchType.superTypeAncestry?.length ?? 0),
    R.indexBy(searchType => searchType.name),
  )

  // Build propDefsIncludingSubtypes from supertypes.
  for (const searchType of Object.values(searchTypes)) {
    searchType.propDefsIncludingSubtypes = R.reduce(
      (prev, m) => ({...prev, ...typeInfo[m].propDefs}),
      {} as {[k: string]: PropDef},
      // superTypeAncestry is ordered [AdminTagged, Title] for example,
      // and doesn't include the current meta-type.
      [...(searchType.superTypeAncestry || []), searchType.name],
    )
  }

  // Augment propDefsIncludingSubtypes from subtypes.
  for (const subType of Object.values(typeInfo)) {
    for (const superType of (subType.superTypeAncestry || []).map(
      m => searchTypes[m],
    )) {
      for (const [propName, subProp] of Object.entries(subType.propDefs)) {
        const superProp = (superType.propDefsIncludingSubtypes[propName] ||= {
          ...subProp,
          _from: subType.name,
        })
        if (subProp.type !== superProp.type) {
          // This is an error in site meta-type configuration, but it doesn't
          // prevent us from working.
          log
            .once(propName)
            ?.warn(
              `type conflict for ${propName}: ${superProp._from ?? superType.name}=${superProp.type} ${subType.name}=${subProp.type}`,
            )
        }
      }
    }
  }

  return searchTypes
}

/**
 * Fetch /api/type-info and convert to search-types shape including
 * propDefsIncludingSubtypes.
 *
 * Returns undefined while disabled or fetching, TypeInfo when fetched, and
 * null when the API response wasn't suitable for conversion.
 */
export const useTypeInfo = (enabled = true): SearchTypes | null | undefined => {
  const {data} = useApi.typeInfo(enabled)
  const searchTypes = useMemo(
    () => data && upgradeTypeInfoToSearchTypes(data),
    [data],
  )
  return searchTypes
}
